import { useState } from "react";
import "@/App.css";
import { Music, Mail, ExternalLink, ChevronDown, Instagram, X, MapPin, Calendar, Users, Headphones } from "lucide-react";

// Professional Photos
const heroImage = "https://customer-assets.emergentagent.com/job_slide-to-web-1/artifacts/yrnl7kn8_The%20Rickmers%27%20Celebration-9163.jpg";
const bioImage = "https://customer-assets.emergentagent.com/job_slide-to-web-1/artifacts/r2ik1vma_PHOTO-2025-02-23-19-15-08.jpg";
const galleryImages = [
  { src: "https://customer-assets.emergentagent.com/job_slide-to-web-1/artifacts/8zu8vlxp_RT-5.jpg", caption: "Ritual London" },
  { src: "https://customer-assets.emergentagent.com/job_slide-to-web-1/artifacts/9jk4n9a0_IMG_6446.JPG", caption: "Club Night" },
  { src: "https://customer-assets.emergentagent.com/job_slide-to-web-1/artifacts/ulrnlv4l_IMG_1238.PNG", caption: "Annabels" },
  { src: "https://customer-assets.emergentagent.com/job_slide-to-web-1/artifacts/0ug91izw_The%20Rickmers%27%20Celebration--5.jpg", caption: "Private Event" },
  { src: "https://customer-assets.emergentagent.com/job_slide-to-web-1/artifacts/xjfi7dpe_PHOTO-2020-05-04-11-29-39.jpg", caption: "Club Night" },
  { src: "https://customer-assets.emergentagent.com/job_slide-to-web-1/artifacts/z966fog8_IMG_0385.jpg", caption: "Annabel's" },
  { src: "https://customer-assets.emergentagent.com/job_slide-to-web-1/artifacts/k9cg3p61_The%20Rickmers%27%20Celebration--17.jpg", caption: "Live Performance" },
  { src: "https://customer-assets.emergentagent.com/job_slide-to-web-1/artifacts/2xm94yj1_34096ec7-b716-4f26-90e7-9a25e10f17fe.png", caption: "Amsterdam" },
  { src: "https://customer-assets.emergentagent.com/job_slide-to-web-1/artifacts/oxlzaddd_10151011976768658.jpg", caption: "Festival Set" }
];

// DJ Collaborations
const djCollabs = [
  { src: "https://customer-assets.emergentagent.com/job_slide-to-web-1/artifacts/hez19bog_With%20Black%20Coffee.jpg", name: "Black Coffee" },
  { src: "https://customer-assets.emergentagent.com/job_slide-to-web-1/artifacts/ggnz1mvk_With%20Bob%20Sinclar.jpg", name: "Bob Sinclar" },
  { src: "https://customer-assets.emergentagent.com/job_slide-to-web-1/artifacts/9j3148n5_With%20Camelphat.jpg", name: "Camelphat" },
  { src: "https://customer-assets.emergentagent.com/job_slide-to-web-1/artifacts/ixgtk4tp_With%20Luciano.PNG", name: "Luciano" },
  { src: "https://customer-assets.emergentagent.com/job_slide-to-web-1/artifacts/83sef612_With%20Sasha.JPG", name: "Sasha" }
];

// Venue logos
const venueLogos = [
  { name: "Annabel's", logo: "https://customer-assets.emergentagent.com/job_slide-to-web-1/artifacts/z8z07q2m_annabels-black-small2.svg", className: "", link: "https://www.instagram.com/annabelsmayfair" },
  { name: "The Box Soho", logo: "https://customer-assets.emergentagent.com/job_slide-to-web-1/artifacts/ogsczsc9_138a1ed8.png", className: "venue-logo-5hs", link: "https://www.instagram.com/boxsoho" },
  { name: "5 Hertford Street", logo: "https://customer-assets.emergentagent.com/job_slide-to-web-1/artifacts/5kg46kmu_Generated_chart__hertford_street_nobg.png.png", className: "venue-logo-box", link: "https://www.instagram.com/5hertfordstreet" }
];

function App() {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [formData, setFormData] = useState({ name: '', email: '', eventType: '', message: '' });
  const [formSubmitted, setFormSubmitted] = useState(false);

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  const openLightbox = (img) => {
    setLightboxImage(img);
    setLightboxOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    setLightboxImage(null);
    document.body.style.overflow = 'auto';
  };

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to send message');
      }
      
      setFormSubmitted(true);
      setFormData({ name: '', email: '', eventType: '', message: '' });
      setTimeout(() => setFormSubmitted(false), 5000);
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitError(error.message || 'Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="app" data-testid="guy-laurie-portfolio">
      {/* Lightbox */}
      {lightboxOpen && (
        <div className="lightbox" onClick={closeLightbox} data-testid="lightbox">
          <button className="lightbox-close" onClick={closeLightbox}>&times;</button>
          <img src={lightboxImage?.src} alt={lightboxImage?.caption} className="lightbox-image" />
          <p className="lightbox-caption">{lightboxImage?.caption}</p>
        </div>
      )}

      {/* Navigation */}
      <nav className="nav" data-testid="main-navigation">
        <div className="nav-brand" data-testid="nav-brand">Guy Laurie</div>
        <div className="nav-links">
          <button onClick={() => scrollToSection('hero')} data-testid="nav-home">Home</button>
          <button onClick={() => scrollToSection('biography')} data-testid="nav-bio">
            <span className="nav-text-full">Biography</span>
            <span className="nav-text-short">Bio</span>
          </button>
          <button onClick={() => scrollToSection('music')} data-testid="nav-music">Music</button>
          <button onClick={() => scrollToSection('gallery')} data-testid="nav-gallery">Gallery</button>
          <button onClick={() => scrollToSection('contact')} data-testid="nav-contact">Contact</button>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="hero" className="hero-section" data-testid="hero-section">
        <div className="hero-overlay"></div>
        <img src={heroImage} alt="Guy Laurie DJing" className="hero-image" data-testid="hero-image" />
        <div className="hero-content">
          <h1 className="hero-title animate-fade-in" data-testid="hero-title">Guy Laurie</h1>
          <p className="hero-subtitle animate-fade-in-delay" data-testid="hero-subtitle">DJ & Producer</p>
          
          {/* Social Links */}
          <div className="hero-socials animate-fade-in-delay-2">
            <a href="https://www.instagram.com/myguymusic" target="_blank" rel="noopener noreferrer" className="social-link" data-testid="social-instagram">
              <Instagram size={24} />
            </a>
            <a href="https://soundcloud.com/myguymusic" target="_blank" rel="noopener noreferrer" className="social-link" data-testid="social-linktree">
              <ExternalLink size={24} />
            </a>
          </div>

          <button 
            className="hero-cta animate-fade-in-delay-3" 
            onClick={() => scrollToSection('biography')}
            data-testid="hero-cta-button"
          >
            <span>Discover More</span>
            <ChevronDown size={20} className="bounce-icon" />
          </button>
        </div>
      </section>

      {/* Biography Section */}
      <section id="biography" className="bio-section" data-testid="biography-section">
        <div className="bio-container">
          <div className="bio-image-wrapper">
            <img src={bioImage} alt="Guy Laurie portrait" className="bio-image" data-testid="bio-image" />
          </div>
          
          <div className="bio-content">
            <div className="bio-block">
              <h2 className="section-label" data-testid="biography-label">BIOGRAPHY</h2>
              <p data-testid="biography-text-1">
                Guy is a resident DJ at several leading London venues and has built his career on a deep understanding of music and audience dynamics. He believes that <em>"The right track at the right time changes everything."</em>
              </p>
              
              <p data-testid="biography-text-2">
                Throughout his career, Guy has performed alongside artists including Chic, Black Coffee, and Mark Ronson. His work has taken him to international stages such as Château de Fontainebleau in France, El Badi Palace in Morocco, and Adam Tower in the Netherlands. He continues to expand his presence across the UK and abroad, maintaining a steady focus on quality, collaboration, and the shared experience of music.
              </p>
            </div>

            {/* Residencies */}
            <div className="residencies-section">
              <h4 className="residencies-title">Current London Residencies</h4>
              <div className="venue-logos">
                {venueLogos.map((venue, i) => (
                  <a key={i} href={venue.link} target="_blank" rel="noopener noreferrer" className="venue-logo-item">
                    <img src={venue.logo} alt={venue.name} className={`venue-logo ${venue.className}`} />
                  </a>
                ))}
              </div>
            </div>
            
            <div className="divider"></div>
            
            <div className="bio-block">
              <h2 className="section-label" data-testid="approach-label">APPROACH</h2>
              <p data-testid="approach-text-1">
                Guy's approach to DJing is all about reading the room in real time. He has the ability to shift seamlessly between genres and tempos, always matching the energy of the crowd and the feel of the space.
              </p>
              <p data-testid="approach-text-2">
                From intimate members' clubs to private events and large dance floors, Guy builds his sets with intent- creating flow through structure, rhythm, and timing. His goal is to create moments where human connection, energy, and sound come together as one.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Music Section */}
      <section id="music" className="music-section" data-testid="music-section">
        <div className="music-container">
          <h2 className="music-title" data-testid="music-title">
            <Music size={32} />
            <span>Listen</span>
          </h2>
          
          {/* Streaming Platforms */}
          <div className="streaming-platforms">
            <a href="https://soundcloud.com/myguymusic" target="_blank" rel="noopener noreferrer" className="platform-btn soundcloud-btn" data-testid="platform-soundcloud">
              <Music size={20} />
              <span>SoundCloud</span>
            </a>
            <a href="https://open.spotify.com/playlist/0wc3QPKMROccuy1Pl2LCUs?si=IDjiKjVxT-S8I5lk6i3fTg" target="_blank" rel="noopener noreferrer" className="platform-btn spotify-btn" data-testid="platform-spotify">
              <Music size={20} />
              <span>Spotify</span>
            </a>
            <a href="https://linktr.ee/guylaurie" target="_blank" rel="noopener noreferrer" className="platform-btn linktree-btn" data-testid="platform-linktree">
              <ExternalLink size={20} />
              <span>Linktree</span>
            </a>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section id="gallery" className="gallery-section" data-testid="gallery-section">
        <h2 className="gallery-title" data-testid="gallery-title">Gallery</h2>
        <p className="gallery-subtitle">Click to view full size</p>
        <div className="gallery-grid">
          {galleryImages.map((img, index) => (
            <div 
              key={index} 
              className="gallery-item" 
              data-testid={`gallery-item-${index}`}
              onClick={() => openLightbox(img)}
            >
              <img 
                src={img.src} 
                alt={img.caption} 
                className={`gallery-image ${index === 7 ? 'gallery-image-right' : ''}`}
              />
              <div className="gallery-overlay">
                <span className="gallery-caption">{img.caption}</span>
              </div>
            </div>
          ))}
        </div>

        {/* DJ Collaborations Collage */}
        <div className="collab-section">
          <h3 className="collab-title">Performing Alongside</h3>
          <div className="collab-grid">
            {djCollabs.map((dj, index) => (
              <div key={index} className="collab-item" onClick={() => openLightbox(dj)}>
                <img src={dj.src} alt={`With ${dj.name}`} className="collab-image" />
                <div className="collab-overlay">
                  <span className="collab-name">{dj.name}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="contact-section" data-testid="contact-section">
        <div className="contact-container">
          <div className="contact-left">
            <h2 className="contact-title">Get In Touch</h2>
            <p className="contact-intro">Interested in booking Guy for your event? Fill out the form and we'll get back to you shortly.</p>
            
            {/* Contact Form */}
            <form className="contact-form" onSubmit={handleFormSubmit} data-testid="contact-form">
              <div className="form-group">
                <input 
                  type="text" 
                  placeholder="Your Name" 
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                  data-testid="form-name"
                />
              </div>
              <div className="form-group">
                <input 
                  type="email" 
                  placeholder="Your Email" 
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  required
                  data-testid="form-email"
                />
              </div>
              <div className="form-group">
                <select 
                  value={formData.eventType}
                  onChange={(e) => setFormData({...formData, eventType: e.target.value})}
                  required
                  data-testid="form-event-type"
                >
                  <option value="">Select Event Type</option>
                  <option value="private">Private Party</option>
                  <option value="corporate">Corporate Event</option>
                  <option value="wedding">Wedding</option>
                  <option value="club">Club Night</option>
                  <option value="festival">Festival</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div className="form-group">
                <textarea 
                  placeholder="Tell us about your event..."
                  value={formData.message}
                  onChange={(e) => setFormData({...formData, message: e.target.value})}
                  rows={4}
                  data-testid="form-message"
                />
              </div>
              {submitError && (
                <div className="form-error" data-testid="form-error">
                  {submitError}
                </div>
              )}
              <button 
                type="submit" 
                className="form-submit" 
                data-testid="form-submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Sending...' : formSubmitted ? 'Message Sent!' : 'Send Inquiry'}
              </button>
            </form>
          </div>
          
          <div className="contact-right">
            <div className="contact-info-card">
              <h3 className="contact-label">DIRECT CONTACT</h3>
              <a 
                href="mailto:info@guylaurie.com" 
                className="contact-email"
                data-testid="contact-email"
              >
                <Mail size={20} />
                info@guylaurie.com
              </a>
              
              <div className="contact-socials">
                <h4>Follow</h4>
                <div className="social-links-row">
                  <a href="https://www.instagram.com/myguymusic" target="_blank" rel="noopener noreferrer" className="contact-social-link">
                    <Instagram size={22} />
                  </a>
                  <a href="https://soundcloud.com/myguymusic" target="_blank" rel="noopener noreferrer" className="contact-social-link">
                    <Music size={22} />
                  </a>
                </div>
              </div>
              
              <div className="availability-note">
                <Calendar size={18} />
                <span>Available for bookings worldwide</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer" data-testid="footer">
        <div className="footer-content">
          <p className="footer-brand">Guy Laurie</p>
          <p className="footer-copy">&copy; {new Date().getFullYear()} All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}

export default App;
